"use client";

import { useId, type ReactNode } from "react";

type Size = "sm" | "md" | "lg";

const BRAND_GRADIENT =
  "linear-gradient(135deg, #a78bfa 0%, #ec4899 55%, #fb923c 100%)";

// peer-focus-visible 링 공통 클래스. visual box 에 얹는다.
// `peer` 입력이 키보드 포커스를 받으면 visual box 에 링이 그려져 화면에도 노출됨.
const FOCUS_RING =
  "peer-focus-visible:ring-2 peer-focus-visible:ring-brand-500 peer-focus-visible:ring-offset-2 peer-focus-visible:ring-offset-white";

// ─────────────────────────────────────────────────────────────
// 1. Checkbox — 기본 체크박스 (브랜드 그라디언트 체크 상태)
// ─────────────────────────────────────────────────────────────
type CheckboxProps = {
  checked: boolean;
  onChange?: (next: boolean) => void;
  label?: ReactNode;
  description?: ReactNode;
  disabled?: boolean;
  error?: boolean;
  size?: Size;
  name?: string;
};

const CHECKBOX_SIZES: Record<
  Size,
  { box: number; radius: number; strokeW: number; label: number; gap: number }
> = {
  sm: { box: 16, radius: 4, strokeW: 2.5, label: 13, gap: 8 },
  md: { box: 20, radius: 6, strokeW: 2.8, label: 15, gap: 10 },
  lg: { box: 24, radius: 7, strokeW: 3, label: 17, gap: 12 },
};

export function Checkbox({
  checked,
  onChange,
  label,
  description,
  disabled,
  error,
  size = "md",
  name,
}: CheckboxProps) {
  const id = useId();
  const s = CHECKBOX_SIZES[size];

  return (
    <label
      htmlFor={id}
      className="font-body inline-flex select-none"
      style={{
        alignItems: description ? "flex-start" : "center",
        gap: s.gap,
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.45 : 1,
      }}
    >
      <input
        id={id}
        name={name}
        type="checkbox"
        checked={!!checked}
        disabled={disabled}
        onChange={(e) => onChange?.(e.target.checked)}
        className="peer sr-only"
      />
      <span
        aria-hidden="true"
        className={`inline-flex flex-shrink-0 items-center justify-center ${FOCUS_RING}`}
        style={{
          width: s.box,
          height: s.box,
          borderRadius: s.radius,
          background: checked ? BRAND_GRADIENT : "#fff",
          border: checked
            ? "1px solid transparent"
            : `1.5px solid ${error ? "#ef4444" : "#e9d5ff"}`,
          boxShadow: checked
            ? "0 6px 16px -6px rgba(236, 72, 153, 0.45)"
            : "0 1px 2px rgba(167,139,250,0.08)",
          transition:
            "background 180ms cubic-bezier(0.16, 1, 0.3, 1), border-color 180ms cubic-bezier(0.16, 1, 0.3, 1), box-shadow 180ms cubic-bezier(0.16, 1, 0.3, 1)",
          marginTop: description ? 2 : 0,
        }}
      >
        <svg
          width={s.box * 0.65}
          height={s.box * 0.65}
          viewBox="0 0 24 24"
          fill="none"
          style={{
            stroke: "#fff",
            strokeWidth: s.strokeW,
            strokeLinecap: "round",
            strokeLinejoin: "round",
            opacity: checked ? 1 : 0,
            transform: `scale(${checked ? 1 : 0.5})`,
            transition:
              "opacity 160ms ease-out, transform 200ms cubic-bezier(0.16, 1, 0.3, 1)",
          }}
        >
          <polyline points="5 12 10 17 19 7" />
        </svg>
      </span>
      {label !== undefined && label !== null && (
        <span className="flex flex-col gap-0.5 leading-snug">
          <span
            style={{
              fontSize: s.label,
              color: error ? "#b91c1c" : "#0f172a",
            }}
          >
            {label}
          </span>
          {description && (
            <span
              style={{ fontSize: s.label - 2, color: "#64748b" }}
            >
              {description}
            </span>
          )}
        </span>
      )}
    </label>
  );
}

// ─────────────────────────────────────────────────────────────
// 2. Radio — 기본 라디오 (브랜드 그라디언트 보더 + 도트)
// ─────────────────────────────────────────────────────────────
type RadioProps = {
  checked: boolean;
  onChange?: (next: boolean) => void;
  label?: ReactNode;
  description?: ReactNode;
  disabled?: boolean;
  error?: boolean;
  size?: Size;
  name?: string;
  value?: string;
};

const RADIO_SIZES: Record<
  Size,
  { box: number; dot: number; label: number; gap: number }
> = {
  sm: { box: 16, dot: 7, label: 13, gap: 8 },
  md: { box: 20, dot: 9, label: 15, gap: 10 },
  lg: { box: 24, dot: 11, label: 17, gap: 12 },
};

export function Radio({
  checked,
  onChange,
  label,
  description,
  disabled,
  error,
  size = "md",
  name,
  value,
}: RadioProps) {
  const id = useId();
  const s = RADIO_SIZES[size];

  return (
    <label
      htmlFor={id}
      className="font-body inline-flex select-none"
      style={{
        alignItems: description ? "flex-start" : "center",
        gap: s.gap,
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.45 : 1,
      }}
    >
      <input
        id={id}
        type="radio"
        name={name}
        value={value}
        checked={!!checked}
        disabled={disabled}
        onChange={(e) => onChange?.(e.target.checked)}
        className="peer sr-only"
      />
      <span
        aria-hidden="true"
        className={[
          "relative inline-flex flex-shrink-0 items-center justify-center rounded-full transition-all duration-200 ease-[cubic-bezier(0.2,0.7,0.3,1)]",
          checked
            ? "bg-brand-gradient shadow-[0_6px_16px_-6px_rgba(236,72,153,0.40)]"
            : error
              ? "border-[1.5px] border-rose-500 bg-white shadow-[0_1px_2px_rgba(167,139,250,0.08)]"
              : "border-[1.5px] border-brand-200 bg-white shadow-[0_1px_2px_rgba(167,139,250,0.08)]",
          FOCUS_RING,
        ].join(" ")}
        style={{
          width: s.box,
          height: s.box,
          marginTop: description ? 2 : 0,
        }}
      >
        {/* 안쪽 흰 원: 그라디언트 테두리를 만들기 위한 2px inset 마스크 */}
        <span
          className={[
            "absolute inset-[2px] rounded-full bg-white transition-opacity duration-150",
            checked ? "opacity-100" : "opacity-0",
          ].join(" ")}
        />
        {/* 중앙 그라디언트 도트 */}
        <span
          className={[
            "relative rounded-full bg-brand-gradient transition-[opacity,transform] duration-200 ease-[cubic-bezier(0.34,1.56,0.64,1)]",
            checked ? "scale-100 opacity-100" : "scale-[0.3] opacity-0",
          ].join(" ")}
          style={{ width: s.dot, height: s.dot }}
        />
      </span>
      {label !== undefined && label !== null && (
        <span className="flex flex-col gap-0.5 leading-snug">
          <span
            style={{
              fontSize: s.label,
              color: error ? "#b91c1c" : "#0f172a",
            }}
          >
            {label}
          </span>
          {description && (
            <span
              style={{ fontSize: s.label - 2, color: "#64748b" }}
            >
              {description}
            </span>
          )}
        </span>
      )}
    </label>
  );
}

// ─────────────────────────────────────────────────────────────
// 3. OptionCard — 카드형 옵션 (TestRunner 스타일 · 큰 터치 타겟)
//
//   kind="radio" 시 role="radio" + aria-checked 로 SR 에 라디오로 노출.
//   라디오 그룹 의미(화살표 키 이동 등)를 온전히 살리려면 부모가 RadioCardGroup
//   패턴으로 감싸는 것이 바람직하나, 본 컴포넌트는 단건 사용도 허용.
// ─────────────────────────────────────────────────────────────
type OptionCardProps = {
  checked: boolean;
  onChange?: (next: boolean) => void;
  label: ReactNode;
  sublabel?: ReactNode;
  kind?: "radio" | "checkbox";
  disabled?: boolean;
  letter?: string;
};

function OptionCard({
  checked,
  onChange,
  label,
  sublabel,
  kind = "radio",
  disabled,
  letter,
}: OptionCardProps) {
  const role = kind === "radio" ? "radio" : "checkbox";
  const ariaProps =
    kind === "radio"
      ? { role, "aria-checked": checked }
      : { role, "aria-checked": checked };
  return (
    <button
      type="button"
      onClick={() => onChange?.(!checked)}
      disabled={disabled}
      {...ariaProps}
      className={[
        "font-body flex w-full min-h-[56px] items-center gap-3.5 rounded-2xl px-4 py-3.5 text-left",
        "transition active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2",
        checked
          ? "border border-transparent bg-brand-gradient text-white shadow-pop"
          : "border-[1.5px] border-brand-100 bg-white text-slate-900 shadow-soft hover:border-brand-300",
        disabled ? "cursor-not-allowed opacity-40" : "cursor-pointer",
      ].join(" ")}
    >
      {letter ? (
        <span
          aria-hidden="true"
          className={[
            "font-display inline-flex h-[30px] w-[30px] flex-shrink-0 items-center justify-center rounded-full text-sm tracking-tight",
            checked ? "bg-white/25 text-white" : "bg-brand-50 text-brand-700",
          ].join(" ")}
        >
          {letter}
        </span>
      ) : (
        <span
          aria-hidden="true"
          className={[
            "inline-flex h-[22px] w-[22px] flex-shrink-0 items-center justify-center transition",
            kind === "radio" ? "rounded-full" : "rounded-md",
            checked
              ? "border-[1.5px] border-white/60 bg-white/25"
              : "border-[1.5px] border-brand-200 bg-white",
          ].join(" ")}
        >
          {checked && kind === "radio" && (
            <span className="size-2 rounded-full bg-white" />
          )}
          {checked && kind === "checkbox" && (
            <svg
              width={14}
              height={14}
              viewBox="0 0 24 24"
              fill="none"
              stroke="#fff"
              strokeWidth={3}
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="5 12 10 17 19 7" />
            </svg>
          )}
        </span>
      )}
      <span className="flex flex-1 flex-col gap-0.5">
        <span className="text-[15px] leading-snug">{label}</span>
        {sublabel && (
          <span
            className={[
              "text-[12.5px] leading-snug",
              checked ? "text-white/85" : "text-slate-400",
            ].join(" ")}
          >
            {sublabel}
          </span>
        )}
      </span>
    </button>
  );
}

// ─────────────────────────────────────────────────────────────
// 4. Chip — 칩형 (rounded-full · 태그/필터/빠른 선택)
//   min-h-[44px] 로 WCAG 2.5.5 터치 타겟 준수.
// ─────────────────────────────────────────────────────────────
type ChipProps = {
  checked: boolean;
  onChange?: (next: boolean) => void;
  label: ReactNode;
  disabled?: boolean;
  kind?: "checkbox" | "radio";
  icon?: ReactNode;
};

function Chip({
  checked,
  onChange,
  label,
  disabled,
  kind = "checkbox",
  icon,
}: ChipProps) {
  const role = kind === "radio" ? "radio" : "checkbox";
  return (
    <button
      type="button"
      onClick={() => onChange?.(!checked)}
      disabled={disabled}
      role={role}
      aria-checked={checked}
      className={[
        "font-display inline-flex min-h-[44px] items-center gap-1.5 rounded-full px-3.5 py-2 text-[13px] tracking-tight transition",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2",
        checked
          ? "bg-brand-gradient text-white shadow-[0_10px_24px_-10px_rgba(236,72,153,0.5)]"
          : "bg-brand-100 text-brand-700",
        disabled ? "cursor-not-allowed opacity-40" : "cursor-pointer",
      ].join(" ")}
    >
      {checked && (
        <svg
          width={12}
          height={12}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={3.2}
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          {kind === "radio" ? (
            <circle cx="12" cy="12" r="6" fill="currentColor" stroke="none" />
          ) : (
            <polyline points="5 12 10 17 19 7" />
          )}
        </svg>
      )}
      {icon && !checked && <span aria-hidden="true">{icon}</span>}
      {label}
    </button>
  );
}

// ─────────────────────────────────────────────────────────────
// 5. Toggle — 토글 스위치 (같은 그라디언트 시스템)
//   role="switch" 로 SR 이 "스위치, 켜짐/꺼짐" 으로 읽도록 함.
// ─────────────────────────────────────────────────────────────
type ToggleProps = {
  checked: boolean;
  onChange?: (next: boolean) => void;
  label?: ReactNode;
  description?: ReactNode;
  disabled?: boolean;
  name?: string;
};

export function Toggle({
  checked,
  onChange,
  label,
  description,
  disabled,
  name,
}: ToggleProps) {
  const id = useId();
  return (
    <label
      htmlFor={id}
      className="font-body inline-flex select-none"
      style={{
        alignItems: description ? "flex-start" : "center",
        gap: 12,
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.45 : 1,
      }}
    >
      <input
        id={id}
        name={name}
        type="checkbox"
        role="switch"
        aria-checked={checked}
        checked={!!checked}
        disabled={disabled}
        onChange={(e) => onChange?.(e.target.checked)}
        className="peer sr-only"
      />
      <span
        aria-hidden="true"
        className={`relative flex-shrink-0 ${FOCUS_RING}`}
        style={{
          width: 40,
          height: 24,
          borderRadius: 12,
          background: checked ? BRAND_GRADIENT : "#e9d5ff",
          transition: "background 220ms ease-out",
          boxShadow: checked
            ? "0 6px 16px -6px rgba(236, 72, 153, 0.45)"
            : "none",
          marginTop: description ? 2 : 0,
        }}
      >
        <span
          style={{
            position: "absolute",
            top: 2,
            left: checked ? 18 : 2,
            width: 20,
            height: 20,
            borderRadius: "50%",
            background: "#fff",
            boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
            transition: "left 220ms cubic-bezier(0.16, 1, 0.3, 1)",
          }}
        />
      </span>
      {label !== undefined && label !== null && (
        <span className="flex flex-col gap-0.5 leading-snug">
          <span className="text-[15px] text-slate-900">{label}</span>
          {description && (
            <span className="text-[13px] text-slate-500">{description}</span>
          )}
        </span>
      )}
    </label>
  );
}
